import { inject, Injectable } from '@angular/core';
import { WorkTimeRegistration } from '@app/shared/types/models/reports/work-time-registration';
import { WorkTimeRegistrationDTO } from '../../dtos/reports/work-time-registration.dto';
import { LocationsDataService } from '../../locations-data.service';
import { TasksDataService } from '../../tasks-data.service';
import { AbstractFactory } from '../abstract.factory';
import { ReportMappers } from './report-mappers';

/**
 * WorkTimeRegistrationFactory - Converts work-time registration DTOs into domain models.
 *
 * Scope: Global (`providedIn: 'root'`).
 *
 * Usage: Used by {@link WorkTimeReportFactory} and {@link AbsenceReportFactory}
 * when producing time report models.
 */
@Injectable({ providedIn: 'root' })
export class WorkTimeRegistrationFactory extends AbstractFactory<WorkTimeRegistrationDTO, WorkTimeRegistration> {
  private readonly tasksService = inject(TasksDataService);
  private readonly locationsService = inject(LocationsDataService);

  public produce(item: WorkTimeRegistrationDTO): WorkTimeRegistration | undefined {
    if (!item) {
      return undefined;
    }

    return new WorkTimeRegistration({
      id: item.id,
      registrationType: ReportMappers.mapRegistrationType(item.registrationType),
      reportUtcDate: item.reportUTCDate ? new Date(item.reportUTCDate) : undefined,
      reportTimeZone: ReportMappers.mapTimeZone(item.reportTimeZone),
      manualHours: item.manualHours,
      rangeFrom: item.rangeFrom ? new Date(item.rangeFrom) : undefined,
      rangeTo: item.rangeTo ? new Date(item.rangeTo) : undefined,
      task: item.taskId ? this.tasksService.getById(item.taskId) : undefined,
      location: item.locationId ? this.locationsService.getById(item.locationId) : undefined,
      description: item.description,
      comments: item.comments,
      hoursType: ReportMappers.mapHoursType(item.hoursType),
      absenceType: ReportMappers.mapAbsenceType(item.absenceType),
      correlationId: item.correlationId,
      projectTaskTimerEntryId: item.projectTaskTimerEntryId
    });
  }
}
